/**
 * All course sections the caller can see.
 *
 * The list is decided by RLS, not by this page: a hub coordinator sees every
 * agency's sections, an agency coordinator sees their own. Nothing here filters
 * by institution, and nothing here should start to.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { STATUS_LABELS, reportingPeriodLabel, sectionCode } from '../lib/sections.js';

export function SectionsPage() {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    apiFetch('/api/v1/classes')
      .then((data) => active && setSections(data ?? []))
      .catch((err) => active && setError(err));
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Course sections</h1>
          <p className="muted">
            Each section is a CCL-coded course with its own schedule, census date and reporting
            period. Instructors run sessions against a published section — they do not create them.
          </p>
        </div>
        <Link className="btn btn--primary btn--auto" to="/sections/new">
          New section
        </Link>
      </div>

      <ErrorBanner error={error} />

      {sections === null && !error && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {sections !== null && sections.length === 0 && (
        <section className="panel">
          <h2>No sections yet</h2>
          <p>
            Build the first one. You will need its CCL code, the State Board–approved hours, and
            the dates it meets — the census date and reporting period are worked out from those.
          </p>
        </section>
      )}

      {sections !== null && sections.length > 0 && (
        <ul className="card-list">
          {sections.map((section) => (
            <li key={section.id}>
              <Link className="card-list__item" to={`/sections/${section.id}`}>
                <span className="card-list__title">
                  {sectionCode(section) && <code>{sectionCode(section)}</code>}{' '}
                  {section.official_ccl_title || section.title}
                </span>
                <span className="card-list__meta muted">
                  <span className={`badge badge--${section.status}`}>
                    {STATUS_LABELS[section.status] ?? section.status}
                  </span>
                  {section.state_approved_hours && ` · ${section.state_approved_hours} approved hours`}
                  {section.delivery_method && ` · ${section.delivery_method}`}
                  {section.reporting_period && ` · ${reportingPeriodLabel(section.reporting_period)}`}
                  {section.census_date && ` · census ${section.census_date}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default SectionsPage;
